import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Stack,
  Group,
  Button,
  Text,
  LoadingOverlay,
  Alert,
  Box,
  Paper,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconX,
  IconDeviceFloppy,
  IconClock,
  IconStarFilled,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useJournal } from '../../../global/entities/Journal/queries/useJournals';
import {
  useCreatePatientJournalEntry,
  useUpdatePatientJournalEntry,
  usePatientJournalEntry,
} from '../../../global/entities/Journal/queries/useJournalEntries';
import {
  usePatientPriorityQuestions,
  useSetPatientPriorityQuestion,
} from '../../../global/entities/Journal/queries/usePatientPriorityQuestions';
import {
  IJournalResponseInput,
  IJournalEntryInput,
  IJournalCategory,
  IPatientPriorityQuestion,
} from '../../../global/entities/Journal/Journal';
import { JournalCategoryStep } from './JournalCategoryStep';
import { inject, observer } from 'mobx-react';
import { useSessionProvider } from '../../../global/provider/sessionContext';
import { PatientStore } from '../../../global/Store/PatientStore';
import { QUESTION_TYPE_IDS } from './QuestionTypes';

interface CategoryProgress {
  categoryId: number;
  isCompleted: boolean;
  responses: IJournalResponseInput[];
}

interface JournalEntryFormProps {
  patientStore?: PatientStore;
}

export const JournalEntryForm: React.FC<JournalEntryFormProps> = inject(
  'patientStore',
)(
  observer(({ patientStore }) => {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();

    const { currentUser } = useSessionProvider();

    // Get URL parameters first
    const {
      journalId,
      entryId: urlEntryId,
      entryDate: urlEntryDate,
    } = useParams();

    const isNewEntry = !urlEntryId;
    const showTimePicker = isNewEntry;
    const [selectedTime, setSelectedTime] = useState(dayjs().format('HH:mm'));
    const selectedTimeRef = useRef(selectedTime);

    const buildEntryDateTime = (): string => {
      if (!isNewEntry)
        return existingEntry?.entryDate || new Date().toISOString();
      if (!urlEntryDate) return new Date().toISOString();
      const [hours, minutes] = selectedTimeRef.current.split(':').map(Number);
      return dayjs(urlEntryDate)
        .hour(hours)
        .minute(minutes)
        .second(0)
        .toISOString();
    };

    const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
    const [categoryProgress, setCategoryProgress] = useState<
      CategoryProgress[]
    >([]);
    const [autoSaveStatus, setAutoSaveStatus] = useState<
      'saving' | 'saved' | 'error' | null
    >(null);
    const [entryId, setEntryId] = useState<number | undefined>(
      urlEntryId ? parseInt(urlEntryId) : undefined,
    );
    const [saveError, setSaveError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Set<number>>(
      new Set(),
    );
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const categoryProgressRef = useRef<CategoryProgress[]>([]);

    const patientId = parseInt(
      patientStore?.patientId || currentUser?.patientID || '0',
    );
    const currentLang = i18n.language as 'en' | 'fr';

    // Fetch journal data
    const { data: journal, isLoading: journalLoading } = useJournal(
      parseInt(journalId || '0'),
    );

    // Fetch existing entry data if editing
    const { data: existingEntry, isLoading: entryLoading } =
      usePatientJournalEntry(entryId || 0);

    // Priority questions
    const { data: priorityQuestions } = usePatientPriorityQuestions(
      parseInt(journalId || '0'),
      patientId,
    );
    const setPatientPriorityMutation = useSetPatientPriorityQuestion();

    // Build priority map for quick lookup
    const priorityMap = useMemo(() => {
      const map = new Map<number, IPatientPriorityQuestion>();
      priorityQuestions?.forEach((pq) => map.set(pq.journalQuestionID, pq));
      return map;
    }, [priorityQuestions]);

    // Build categories with synthetic priority section at index 0
    const categoriesWithPriority = useMemo(() => {
      if (!journal?.categories) return [];

      // Collect priority questions preserving the original display order:
      // categories come pre-sorted from the backend, questions are sorted in JournalCategoryStep
      const priorityQs = journal.categories.flatMap((cat) =>
        [...cat.questions]
          .sort((a, b) => a.order - b.order)
          .filter((q) => {
            const pq = priorityMap.get(q.id);
            if (!pq) return q.isPriority || q.isPriorityLocked;
            return pq.isPriority;
          }),
      );

      if (priorityQs.length === 0) return journal.categories;

      const priorityCategory: IJournalCategory = {
        id: -1, // Synthetic ID
        journalID: parseInt(journalId || '0'),
        nameFr: 'Prioritaires',
        nameEn: 'Priority',
        order: -1,
        questions: priorityQs,
      };

      return [priorityCategory, ...journal.categories];
    }, [journal, priorityMap, journalId]);

    // Mutations
    const createEntryMutation = useCreatePatientJournalEntry();
    const updateEntryMutation = useUpdatePatientJournalEntry();

    // Initialize category progress when journal loads (for new entries)
    useEffect(() => {
      if (
        categoriesWithPriority.length > 0 &&
        categoryProgress.length === 0 &&
        !entryId
      ) {
        const initialProgress = categoriesWithPriority.map((category) => ({
          categoryId: category.id,
          isCompleted: false,
          responses: category.questions.map((question) => ({
            journalQuestionID: question.id,
            responseValue: '',
            subResponseValue: '',
            subResponseMandatory:
              question.questionTypeID ===
              QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT,
            isSkipped: false,
            responseDate: new Date().toISOString(),
          })),
        }));
        setCategoryProgress(initialProgress);
      }
    }, [categoriesWithPriority, categoryProgress.length, entryId]);

    // Load existing entry data and populate form (for existing entries)
    useEffect(() => {
      if (
        categoriesWithPriority.length > 0 &&
        existingEntry &&
        entryId &&
        categoryProgress.length === 0
      ) {
        // Create a map of existing responses by question ID
        const existingResponsesMap = new Map();
        existingEntry.responses.forEach((response) => {
          existingResponsesMap.set(response.journalQuestionID, response);
        });

        // Initialize category progress with existing responses
        const populatedProgress = categoriesWithPriority.map((category) => {
          const categoryResponses = category.questions.map((question) => {
            const existingResponse = existingResponsesMap.get(question.id);
            return {
              journalQuestionID: question.id,
              responseValue: existingResponse?.responseValue || '',
              subResponseValue: existingResponse?.subResponseValue || '',
              subResponseMandatory: question.questionTypeID === 4,
              isSkipped: existingResponse?.isSkipped || false,
              responseDate:
                existingResponse?.responseDate || new Date().toISOString(),
            };
          });

          // Check if category is completed (all required questions answered)
          const hasAllResponses = categoryResponses.every(
            (r) =>
              r.isSkipped || (r.responseValue && r.responseValue.trim() !== ''),
          );

          return {
            categoryId: category.id,
            isCompleted: hasAllResponses,
            responses: categoryResponses,
          };
        });

        setCategoryProgress(populatedProgress || []);

        // Find the last category with answers and position the form there
        let lastAnsweredCategoryIndex = 0;
        for (let i = (populatedProgress?.length || 0) - 1; i >= 0; i--) {
          const categoryHasAnswers = populatedProgress?.[i]?.responses.some(
            (r) =>
              r.isSkipped || (r.responseValue && r.responseValue.trim() !== ''),
          );
          if (categoryHasAnswers) {
            lastAnsweredCategoryIndex = i;
            break;
          }
        }

        // If no categories have answers, start from the first; otherwise, start from the last answered
        setCurrentCategoryIndex(lastAnsweredCategoryIndex);
      }
    }, [
      categoriesWithPriority,
      existingEntry,
      entryId,
      categoryProgress.length,
    ]);

    // Reconcile categoryProgress when priority questions change (patient toggled a star)
    useEffect(() => {
      if (categoryProgress.length === 0 || !journal?.categories) return;

      const hasPriorityPage = categoriesWithPriority[0]?.id === -1;
      const hadPriorityPage = categoryProgress[0]?.categoryId === -1;

      // Build a map of all current response values by questionID for syncing
      const responseMap = new Map<
        number,
        (typeof categoryProgress)[0]['responses'][0]
      >();
      for (const cp of categoryProgress) {
        for (const r of cp.responses) {
          const existing = responseMap.get(r.journalQuestionID);
          if (!existing || r.responseDate > existing.responseDate) 
            responseMap.set(r.journalQuestionID, r);
          
        }
      }

      if (hasPriorityPage) {
        const priorityQuestionsList = categoriesWithPriority[0].questions;
        const newPriorityResponses = priorityQuestionsList.map((q) => {
          const existing = responseMap.get(q.id);
          return existing
            ? { ...existing }
            : {
                journalQuestionID: q.id,
                responseValue: '',
                subResponseValue: '',
                subResponseMandatory:
                  q.questionTypeID ===
                  QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT,
                isSkipped: false,
                responseDate: new Date().toISOString(),
              };
        });

        setCategoryProgress((prev) => {
          const regularPages = hadPriorityPage ? prev.slice(1) : prev;
          const priorityEntry = {
            categoryId: -1,
            isCompleted: newPriorityResponses.every(
              (r) =>
                r.isSkipped ||
                (r.responseValue && r.responseValue.trim() !== ''),
            ),
            responses: newPriorityResponses,
          };
          return [priorityEntry, ...regularPages];
        });
      } else if (hadPriorityPage) {
        // Priority page was removed (no more priority questions)
        setCategoryProgress((prev) => prev.slice(1));
      }
    }, [categoriesWithPriority, journal?.categories]);

    // Keep ref in sync with state for use in async save
    useEffect(() => {
      categoryProgressRef.current = categoryProgress;
    }, [categoryProgress]);

    // Save entry (always as valid, non-draft)
    const saveEntry = useCallback(async () => {
      const progress = categoryProgressRef.current;
      if (!journal || progress.length === 0) return;

      // Only save if at least one answer has been given
      const hasAnyResponse = progress.some((cp) =>
        cp.responses.some(
          (r) => r.responseValue && r.responseValue.trim() !== '',
        ),
      );
      if (!hasAnyResponse) return;

      setAutoSaveStatus('saving');

      try {
        // Deduplicate responses by journalQuestionID (priority questions appear in both
        // the priority section and their original category). Keep the latest responseDate.
        const responseMap = new Map<
          number,
          (typeof progress)[0]['responses'][0]
        >();
        for (const cp of progress) {
          for (const r of cp.responses) {
            const existing = responseMap.get(r.journalQuestionID);
            if (!existing || r.responseDate > existing.responseDate) 
              responseMap.set(r.journalQuestionID, r);
            
          }
        }
        const allResponses = Array.from(responseMap.values());
        const entryData: IJournalEntryInput = {
          patientID: patientId,
          journalID: parseInt(journalId || '0'),
          entryDate: buildEntryDateTime(),
          isCompleted: true,
          isDraft: false,
          notes: existingEntry?.notes,
          responses: allResponses,
        };

        if (entryId) {
          await updateEntryMutation.mutateAsync({
            id: entryId,
            entry: entryData,
          });
        } else {
          const newEntryId = await createEntryMutation.mutateAsync(entryData);
          setEntryId(newEntryId);
        }

        setAutoSaveStatus('saved');
        setSaveError(null);
        setTimeout(() => setAutoSaveStatus(null), 2000);
      } catch (error: any) {
        console.error('Save failed:', error);
        setAutoSaveStatus('error');
        setSaveError(null);
        setTimeout(() => setAutoSaveStatus(null), 3000);
      }
    }, [
      journal,
      patientId,
      journalId,
      entryId,
      existingEntry,
      createEntryMutation,
      updateEntryMutation,
      urlEntryDate,
    ]);

    // Debounced save triggered on each answer change or time-field edit
    const hasPendingSaveRef = useRef(false);
    const debouncedSave = useCallback(() => {
      hasPendingSaveRef.current = true;
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        hasPendingSaveRef.current = false;
        saveEntry();
      }, 800);
    }, [saveEntry]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => clearTimeout(saveTimeoutRef.current);
    }, []);

    // Handle direct category navigation from progress bar
    const handleCategoryNavigation = (targetIndex: number) => {
      // If there are validation errors, don't allow navigation
      if (validationErrors.size > 0) {
        scrollToFirstValidationError();
        return;
      }

      setCurrentCategoryIndex(targetIndex);

      // Auto-scroll to top of the shell container after page change
      setTimeout(() => {
        const shellContainer = document.getElementById(
          'patient-dashboard-container',
        );
        if (shellContainer) {
          shellContainer.scrollTo({
            top: 100,
            behavior: 'smooth',
          });
        }
      }, 100);
    };

    // Progress line component
    const ProgressLine: React.FC = () => {
      if (!journal) return null;

      const categories = categoriesWithPriority;
      const totalCategories = categories?.length ?? 0;

      return (
        <Box mb="xl">
          <Group justify="space-between" mb="md">
            <Text size="sm" c="dimmed">
              {t(
                'journal.categoryProgress',
                `Category ${currentCategoryIndex + 1} of ${totalCategories}`,
              )}
            </Text>
            <Group gap="xs">
              {autoSaveStatus && (
                <Group gap={4}>
                  {autoSaveStatus === 'saving' && (
                    <>
                      <IconDeviceFloppy size={14} color="blue" />
                      <Text size="xs" c="blue">
                        {t('journal.saving')}
                      </Text>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <IconCheck size={14} color="green" />
                      <Text size="xs" c="green">
                        {t('journal.saved')}
                      </Text>
                    </>
                  )}
                  {autoSaveStatus === 'error' && (
                    <>
                      <IconX size={14} color="red" />
                      <Text size="xs" c="red">
                        {t('journal.saveError')}
                      </Text>
                    </>
                  )}
                </Group>
              )}
            </Group>
          </Group>

          <Box style={{ position: 'relative' }}>
            {/* Progress line background */}
            <Box
              style={{
                height: 2,
                backgroundColor: 'var(--mantine-color-gray-3)',
                borderRadius: 1,
                position: 'relative',
              }}
            />

            {/* Progress line filled */}
            <Box
              style={{
                height: 2,
                backgroundColor: 'var(--mantine-color-blue-5)',
                borderRadius: 1,
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${(currentCategoryIndex / Math.max(totalCategories - 1, 1)) * 100}%`,
                transition: 'width 0.3s ease',
              }}
            />

            {/* Category dots */}
            <Group
              gap={0}
              style={{ position: 'absolute', top: -6, left: 0, right: 0 }}
            >
              {categories?.map((category, index) => {
                const isCompleted =
                  categoryProgress[index]?.isCompleted || false;
                const isCurrent = index === currentCategoryIndex;
                const isPassed = index < currentCategoryIndex;

                return (
                  <Box
                    key={category.id}
                    style={{
                      flex: 1,
                      display: 'flex',
                      justifyContent:
                        index === 0
                          ? 'flex-start'
                          : index === (categories?.length ?? 0) - 1
                            ? 'flex-end'
                            : 'center',
                    }}
                  >
                    <Box
                      onClick={() => handleCategoryNavigation(index)}
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor:
                          isCompleted || isPassed
                            ? 'var(--mantine-color-blue-5)'
                            : isCurrent
                              ? 'var(--mantine-color-blue-2)'
                              : 'var(--mantine-color-gray-3)',
                        border: isCurrent
                          ? '3px solid var(--mantine-color-blue-5)'
                          : '2px solid var(--mantine-color-white)',
                        boxShadow: '0 0 0 1px var(--mantine-color-gray-4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        ':hover': {
                          transform: 'scale(1.2)',
                          boxShadow: '0 0 0 2px var(--mantine-color-blue-4)',
                        },
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.2)';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 2px var(--mantine-color-blue-4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 1px var(--mantine-color-gray-4)';
                      }}
                    >
                      {category.id === -1 ? (
                        <IconStarFilled
                          size={8}
                          color={
                            isCompleted || isPassed || isCurrent
                              ? 'white'
                              : 'var(--mantine-color-yellow-5)'
                          }
                        />
                      ) : (
                        (isCompleted || isPassed) && (
                          <IconCheck size={8} color="white" />
                        )
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Group>
          </Box>

          {/* Category names */}
          <Group gap={0} mt="sm">
            {categories?.map((category, index) => (
              <Box
                key={category.id}
                style={{
                  flex: 1,
                  textAlign:
                    index === 0
                      ? 'left'
                      : index === categories.length - 1
                        ? 'right'
                        : 'center',
                  cursor: 'pointer',
                }}
                onClick={() => handleCategoryNavigation(index)}
              >
                <Text
                  size="xs"
                  c={index === currentCategoryIndex ? 'blue' : 'dimmed'}
                  fw={index === currentCategoryIndex ? 600 : 400}
                  style={{
                    transition: 'color 0.2s ease',
                    userSelect: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (index !== currentCategoryIndex)
                      {e.currentTarget.style.color =
                        'var(--mantine-color-blue-6)';}
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentCategoryIndex)
                      {e.currentTarget.style.color =
                        'var(--mantine-color-dimmed)';}
                  }}
                >
                  {currentLang === 'fr' ? category.nameFr : category.nameEn}
                </Text>
              </Box>
            ))}
          </Group>
        </Box>
      );
    };

    // Handle category response updates — triggers debounced save on each answer
    // Also syncs shared responses across pages (priority questions appear on multiple pages)
    const handleCategoryResponsesChange = useCallback(
      (responses: IJournalResponseInput[]) => {
        setCategoryProgress((prev) => {
          const newProgress = [...prev];
          if (newProgress[currentCategoryIndex]) {
            newProgress[currentCategoryIndex].responses = responses;
            const hasAllResponses = responses.every(
              (r) =>
                r.isSkipped ||
                (r.responseValue && r.responseValue.trim() !== ''),
            );
            newProgress[currentCategoryIndex].isCompleted = hasAllResponses;

            // Sync shared responses: if a question appears on another page, update it there too
            for (const updatedResponse of responses) {
              for (let i = 0; i < newProgress.length; i++) {
                if (i === currentCategoryIndex) continue;
                const otherResponses = newProgress[i].responses;
                const matchIdx = otherResponses.findIndex(
                  (r) =>
                    r.journalQuestionID === updatedResponse.journalQuestionID,
                );
                if (matchIdx !== -1) 
                  otherResponses[matchIdx] = { ...updatedResponse };
                
              }
            }
          }
          return newProgress;
        });
        debouncedSave();
      },
      [currentCategoryIndex, debouncedSave],
    );

    // Handle validation errors from questions
    const handleValidationChange = useCallback(
      (hasErrors: boolean, errorQuestionIds?: number[]) => {
        if (!hasErrors) setValidationErrors(new Set());
        else if (errorQuestionIds)
          setValidationErrors(new Set(errorQuestionIds));
      },
      [],
    );

    // Scroll to the first question with validation error
    const scrollToFirstValidationError = () => {
      if (validationErrors.size === 0) return;

      const firstErrorQuestionId = Array.from(validationErrors)[0];
      const questionElement = document.querySelector(
        `[data-question-id="${firstErrorQuestionId}"]`,
      );

      if (questionElement) {
        // First scroll to the question
        questionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        // Then add a highlight effect
        questionElement.classList.add('question-error-highlight');
        setTimeout(() => {
          questionElement.classList.remove('question-error-highlight');
        }, 3000);
      }
    };

    // Navigation handlers
    const handlePrevious = () => {
      // If there are validation errors, scroll to the first error instead of navigating
      if (validationErrors.size > 0) {
        scrollToFirstValidationError();
        return;
      }

      if (currentCategoryIndex > 0) {
        setCurrentCategoryIndex(currentCategoryIndex - 1);
        // Auto-scroll to top of the shell container after page change
        setTimeout(() => {
          // Use ID selector for maximum reliability
          const shellContainer = document.getElementById(
            'patient-dashboard-container',
          );
          if (shellContainer) {
            shellContainer.scrollTo({
              top: 100,
              behavior: 'smooth',
            });
          }
        }, 100);
      }
    };

    const handleNext = () => {
      // If there are validation errors, scroll to the first error instead of navigating
      if (validationErrors.size > 0) {
        scrollToFirstValidationError();
        return;
      }

      if (currentCategoryIndex < categoriesWithPriority.length - 1) {
        setCurrentCategoryIndex(currentCategoryIndex + 1);
        // Auto-scroll to top of the shell container after page change
        setTimeout(() => {
          // Use ID selector for maximum reliability
          const shellContainer = document.getElementById(
            'patient-dashboard-container',
          );
          if (shellContainer) {
            shellContainer.scrollTo({
              top: 100,
              behavior: 'smooth',
            });
          }
        }, 100);
      }
    };

    // Done — flush any pending save before leaving so an unsaved edit isn't lost
    const handleDone = async () => {
      clearTimeout(saveTimeoutRef.current);
      if (hasPendingSaveRef.current) {
        hasPendingSaveRef.current = false;
        await saveEntry();
      }
      navigate('/patientDashboard/journals');
    };

    // Navigation is handled by the buttons

    if (journalLoading || entryLoading) {
      return (
        <Container size="md" py="md">
          <LoadingOverlay visible />
        </Container>
      );
    }

    if (!journal) {
      return (
        <Container size="md" py="md">
          <Alert color="red" title={t('journal.notFound')}>
            {t('journal.journalNotFoundMessage')}
          </Alert>
        </Container>
      );
    }

    const currentCategory = categoriesWithPriority[currentCategoryIndex];
    const isLastCategory =
      currentCategoryIndex === categoriesWithPriority.length - 1;

    return (
      <Container size="md" py="md" style={{ width: '100%', maxWidth: '100%' }}>
        <Stack gap="lg">
          {/* Time picker for new entries */}
          {showTimePicker && (
            <Paper p="md" withBorder>
              <TimeInput
                label={t('journal.entryTime')}
                description={t('journal.entryTimeDescription', {
                  date: dayjs(urlEntryDate).format('YYYY-MM-DD'),
                })}
                leftSection={<IconClock size={16} />}
                value={selectedTime}
                onChange={(event) => {
                  const newTime = event.currentTarget.value;
                  selectedTimeRef.current = newTime;
                  setSelectedTime(newTime);
                  debouncedSave();
                }}
              />
            </Paper>
          )}

          {/* Progress indicator */}
          <ProgressLine />

          {/* Current category step */}
          {currentCategory && (
            <JournalCategoryStep
              category={currentCategory}
              responses={
                categoryProgress[currentCategoryIndex]?.responses || []
              }
              onResponsesChange={handleCategoryResponsesChange}
              onValidationChange={handleValidationChange}
              priorityMap={priorityMap}
              onTogglePriority={(questionId, isPriority) => {
                setPatientPriorityMutation.mutate({
                  patientId,
                  questionId,
                  isPriority,
                });
              }}
            />
          )}

          {/* Error message */}
          {saveError && (
            <Alert color="red" title={t('journal.error', 'Error')}>
              {saveError}
            </Alert>
          )}

          {/* Navigation buttons */}
          <Paper p="md" withBorder>
            <Group justify="space-between">
              <Button
                variant="outline"
                leftSection={<IconArrowLeft size={16} />}
                onClick={handlePrevious}
                disabled={currentCategoryIndex === 0}
              >
                {t('journal.previous')}
              </Button>

              <Group>
                {!isLastCategory && (
                  <Button
                    rightSection={<IconArrowRight size={16} />}
                    onClick={handleNext}
                  >
                    {t('journal.next')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleDone}
                  leftSection={<IconCheck size={16} />}
                >
                  {t('journal.done')}
                </Button>
              </Group>
            </Group>
          </Paper>
        </Stack>
      </Container>
    );
  }),
);
